"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { cn } from "../../../utils";
import { Button } from "../../base/button/button";
import { ListboxColumn } from "./ListboxColumn";
export const LISTBOX_DND_MIME = "application/x-bliks-listbox";
export function parseListboxDragPayload(raw) {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if ((parsed.side === "left" || parsed.side === "right") && Array.isArray(parsed.ids) && parsed.ids.every((id)=>typeof id === "string")) {
            return {
                side: parsed.side,
                ids: parsed.ids
            };
        }
        return null;
    } catch  {
        return null;
    }
}
function Listbox({ value = [], onChange, items = [], leftLabel = "Available", rightLabel = "Selected", searchPlaceholder = "Search...", renderItem, disabled = false, getItemId = (item)=>item.id, searchFilter, className, availableFilterSlot, availableFilter, lockedIds, lockedHint }) {
    const lockedSet = React.useMemo(()=>new Set(lockedIds ?? []), [
        lockedIds
    ]);
    const [leftSearch, setLeftSearch] = React.useState("");
    const [rightSearch, setRightSearch] = React.useState("");
    const [leftSelected, setLeftSelected] = React.useState(new Set());
    const [rightSelected, setRightSelected] = React.useState(new Set());
    const [lastLeftClickedIndex, setLastLeftClickedIndex] = React.useState(null);
    const [lastRightClickedIndex, setLastRightClickedIndex] = React.useState(null);
    // Split items into available and selected. A locked id belongs to the right
    // column whether or not `value` carries it — that is what pinning means, and
    // the caller may hand over a lock the value has not caught up with yet.
    const availableItems = React.useMemo(()=>{
        return items.filter((item)=>{
            const id = getItemId(item);
            return !value.includes(id) && !lockedSet.has(id);
        });
    }, [
        items,
        value,
        getItemId,
        lockedSet
    ]);
    const selectedItems = React.useMemo(()=>{
        return items.filter((item)=>{
            const id = getItemId(item);
            return value.includes(id) || lockedSet.has(id);
        });
    }, [
        items,
        value,
        getItemId,
        lockedSet
    ]);
    // Default search filter
    const defaultSearchFilter = React.useCallback((item, query)=>{
        const searchableValue = JSON.stringify(item).toLowerCase();
        return searchableValue.includes(query.toLowerCase());
    }, []);
    const filterFunction = searchFilter || defaultSearchFilter;
    // Filter items based on search + optional availableFilter (left side only)
    const filteredAvailable = React.useMemo(()=>{
        let result = availableItems;
        if (availableFilter) {
            result = result.filter(availableFilter);
        }
        if (leftSearch) {
            result = result.filter((item)=>filterFunction(item, leftSearch));
        }
        return result;
    }, [
        availableItems,
        availableFilter,
        leftSearch,
        filterFunction
    ]);
    const filteredSelected = React.useMemo(()=>{
        if (!rightSearch) return selectedItems;
        return selectedItems.filter((item)=>filterFunction(item, rightSearch));
    }, [
        selectedItems,
        rightSearch,
        filterFunction
    ]);
    // Move items to right column
    const moveToSelected = React.useCallback(()=>{
        if (disabled) return;
        const newValue = [
            ...value,
            ...Array.from(leftSelected)
        ];
        onChange(newValue);
        setLeftSelected(new Set());
    }, [
        value,
        leftSelected,
        onChange,
        disabled
    ]);
    // Move items to left column
    const moveToAvailable = React.useCallback(()=>{
        if (disabled) return;
        const newValue = value.filter((id)=>!rightSelected.has(id) || lockedSet.has(id));
        onChange(newValue);
        setRightSelected(new Set());
    }, [
        value,
        rightSelected,
        onChange,
        disabled,
        lockedSet
    ]);
    // Drop handlers — accept explicit ids so a non-selected dragged item still moves correctly.
    const dropToRight = React.useCallback((ids)=>{
        if (disabled) return;
        const toAdd = ids.filter((id)=>!value.includes(id));
        if (toAdd.length === 0) return;
        onChange([
            ...value,
            ...toAdd
        ]);
        setLeftSelected((prev)=>{
            const next = new Set(prev);
            toAdd.forEach((id)=>next.delete(id));
            return next;
        });
    }, [
        value,
        onChange,
        disabled
    ]);
    const dropToLeft = React.useCallback((ids)=>{
        if (disabled) return;
        const toRemove = new Set(ids.filter((id)=>!lockedSet.has(id)));
        const newValue = value.filter((id)=>!toRemove.has(id));
        if (newValue.length === value.length) return;
        onChange(newValue);
        setRightSelected((prev)=>{
            const next = new Set(prev);
            ids.forEach((id)=>next.delete(id));
            return next;
        });
    }, [
        value,
        onChange,
        disabled,
        lockedSet
    ]);
    // Toggle item selection with shift-click range support
    const toggleLeftItem = React.useCallback((itemId, index, shiftKey)=>{
        if (shiftKey && lastLeftClickedIndex !== null) {
            // Range selection
            const start = Math.min(lastLeftClickedIndex, index);
            const end = Math.max(lastLeftClickedIndex, index);
            const rangeIds = filteredAvailable.slice(start, end + 1).map(getItemId);
            setLeftSelected((prev)=>{
                const newSet = new Set(prev);
                rangeIds.forEach((id)=>newSet.add(id));
                return newSet;
            });
        } else {
            // Single toggle
            setLeftSelected((prev)=>{
                const newSet = new Set(prev);
                if (newSet.has(itemId)) {
                    newSet.delete(itemId);
                } else {
                    newSet.add(itemId);
                }
                return newSet;
            });
        }
        setLastLeftClickedIndex(index);
    }, [
        lastLeftClickedIndex,
        filteredAvailable,
        getItemId
    ]);
    const toggleRightItem = React.useCallback((itemId, index, shiftKey)=>{
        if (lockedSet.has(itemId)) return;
        if (shiftKey && lastRightClickedIndex !== null) {
            // Range selection
            const start = Math.min(lastRightClickedIndex, index);
            const end = Math.max(lastRightClickedIndex, index);
            const rangeIds = filteredSelected.slice(start, end + 1).map(getItemId).filter((id)=>!lockedSet.has(id));
            setRightSelected((prev)=>{
                const newSet = new Set(prev);
                rangeIds.forEach((id)=>newSet.add(id));
                return newSet;
            });
        } else {
            // Single toggle
            setRightSelected((prev)=>{
                const newSet = new Set(prev);
                if (newSet.has(itemId)) {
                    newSet.delete(itemId);
                } else {
                    newSet.add(itemId);
                }
                return newSet;
            });
        }
        setLastRightClickedIndex(index);
    }, [
        lastRightClickedIndex,
        filteredSelected,
        getItemId,
        lockedSet
    ]);
    // Double-click handlers
    const handleLeftDoubleClick = React.useCallback((itemId)=>{
        if (disabled) return;
        onChange([
            ...value,
            itemId
        ]);
    }, [
        value,
        onChange,
        disabled
    ]);
    const handleRightDoubleClick = React.useCallback((itemId)=>{
        if (disabled || lockedSet.has(itemId)) return;
        onChange(value.filter((id)=>id !== itemId));
    }, [
        value,
        onChange,
        disabled,
        lockedSet
    ]);
    // Select all handlers
    const handleSelectAllLeft = React.useCallback((checked)=>{
        if (checked) {
            setLeftSelected(new Set(filteredAvailable.map(getItemId)));
        } else {
            setLeftSelected(new Set());
        }
    }, [
        filteredAvailable,
        getItemId
    ]);
    const selectableRightIds = React.useMemo(()=>filteredSelected.map(getItemId).filter((id)=>!lockedSet.has(id)), [
        filteredSelected,
        getItemId,
        lockedSet
    ]);
    const handleSelectAllRight = React.useCallback((checked)=>{
        setRightSelected(checked ? new Set(selectableRightIds) : new Set());
    }, [
        selectableRightIds
    ]);
    // Check if all items are selected
    const allLeftSelected = filteredAvailable.length > 0 && leftSelected.size === filteredAvailable.length;
    const allRightSelected = selectableRightIds.length > 0 && rightSelected.size === selectableRightIds.length;
    return /*#__PURE__*/ _jsxs("div", {
        className: cn("flex items-stretch gap-4", disabled && "pointer-events-none opacity-50", className),
        children: [
            /*#__PURE__*/ _jsx(ListboxColumn, {
                side: "left",
                label: leftLabel,
                count: filteredAvailable.length,
                searchValue: leftSearch,
                onSearchChange: setLeftSearch,
                searchPlaceholder: searchPlaceholder,
                items: filteredAvailable,
                selectedItems: leftSelected,
                onToggleItem: toggleLeftItem,
                onDoubleClickItem: handleLeftDoubleClick,
                renderItem: renderItem,
                getItemId: getItemId,
                allSelected: allLeftSelected,
                onSelectAll: handleSelectAllLeft,
                filterSlot: availableFilterSlot,
                onDropItems: dropToLeft
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col justify-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        type: "button",
                        variant: "outline",
                        size: "icon",
                        onClick: moveToSelected,
                        disabled: disabled || leftSelected.size === 0,
                        title: "Move selected to right",
                        children: /*#__PURE__*/ _jsx(ArrowRight, {
                            className: "h-4 w-4"
                        })
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        type: "button",
                        variant: "outline",
                        size: "icon",
                        onClick: moveToAvailable,
                        disabled: disabled || rightSelected.size === 0,
                        title: "Move selected to left",
                        children: /*#__PURE__*/ _jsx(ArrowLeft, {
                            className: "h-4 w-4"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ListboxColumn, {
                side: "right",
                label: rightLabel,
                count: filteredSelected.length,
                searchValue: rightSearch,
                onSearchChange: setRightSearch,
                searchPlaceholder: searchPlaceholder,
                items: filteredSelected,
                selectedItems: rightSelected,
                onToggleItem: toggleRightItem,
                onDoubleClickItem: handleRightDoubleClick,
                renderItem: renderItem,
                getItemId: getItemId,
                allSelected: allRightSelected,
                onSelectAll: handleSelectAllRight,
                onDropItems: dropToRight,
                selectableCount: selectableRightIds.length,
                lockedIds: lockedSet,
                lockedHint: lockedHint
            })
        ]
    });
}
Listbox.displayName = "Listbox";
export { Listbox };

//# sourceMappingURL=listbox.js.map